import React, { useState } from "react";
import { Typography, Button } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  MinusOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import InrOutlined from "../common/InrOutlined";
import dealVoiceLogo from "../../assets/logo/dealVoice.png";

const { Title, Paragraph } = Typography;

const COMPETITORS = [
  {
    id: "exotel_telephony",
    name: "Exotel / Telephony",
    subtitle: "Cloud Telephony Trunking",
  },
  {
    id: "spreadsheets",
    name: "Excel & Sheets",
    subtitle: "Manual Disconnected Spreadsheets",
  },
  {
    id: "zoho_crm",
    name: "Zoho CRM",
    subtitle: "Zoho Sales Cloud",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    subtitle: "Enterprise Sales Cloud",
  },
  {
    id: "leadsquared",
    name: "LeadSquared",
    subtitle: "Lead & Field Automation",
  },
];

const COMPARISON_ROWS = [
  {
    feature: "Native Android Telephony Sync",
    desc: "Background sync with real call durations, logs & timestamps without expensive CTI hardware.",
    dealvoice: {
      status: "check-green",
      title: "100% Native & Automated",
      sub: "Direct SIM/Mobile carrier integration",
    },
    exotel_telephony: {
      status: "warn-amber",
      title: "Virtual numbers only",
      sub: "Expensive per-minute rates + trunk lag",
    },
    spreadsheets: {
      status: "close-gray",
      title: "No call capture",
      sub: "100% manual, biased agent logging",
    },
    zoho_crm: {
      status: "warn-amber",
      title: "Add-on telephony plugin",
      sub: "Requires third-party PBX connector & monthly surcharges",
    },
    salesforce: {
      status: "warn-amber",
      title: "Open CTI hardware integration",
      sub: "Requires expensive CTI adapter & telephony maintenance",
    },
    leadsquared: {
      status: "warn-amber",
      title: "Softphone add-on only",
      sub: "Requires dedicated VoIP trunks & agent per-minute add-ons",
    },
  },
  {
    feature: "7-Day Stagnant Deal Warning Radar",
    desc: "Automated alert engine that stops high-intent deals from rotting across pipeline stages.",
    dealvoice: {
      status: "check-green",
      title: "Built-in automated alert & manager escalation",
      sub: null,
    },
    exotel_telephony: {
      status: "close-gray",
      title: "Not available",
      sub: "Telephony provider only",
    },
    spreadsheets: {
      status: "close-gray",
      title: "Zero deal stage alerts",
      sub: "Static cells, zero stale detection",
    },
    zoho_crm: {
      status: "warn-amber",
      title: "Manual workflow config",
      sub: "Requires custom blueprint/workflow rule scripting",
    },
    salesforce: {
      status: "warn-amber",
      title: "Complex Flow Builder",
      sub: "Requires Flow development, custom triggers & maintenance",
    },
    leadsquared: {
      status: "warn-amber",
      title: "Rule-based triggers only",
      sub: "Requires setting up multi-tier automation rules manually",
    },
  },
  {
    feature: "Unified Quotation, Proposal & Invoicing",
    desc: "Sync multi-version quotes, interactive proposals & GST invoices with Razorpay payment links.",
    dealvoice: {
      status: "check-green",
      title: "End-to-end multi-entity sync",
      sub: "Automated 18% GST CPQ calculation",
    },
    exotel_telephony: {
      status: "close-gray",
      title: "Not available",
      sub: "Requires separate accounting SaaS",
    },
    spreadsheets: {
      status: "close-gray",
      title: "Disconnected templates",
      sub: "Manual quote generation errors",
    },
    zoho_crm: {
      status: "warn-amber",
      title: "Separate Zoho Books sync",
      sub: "Requires external Zoho Books/Invoice subscription",
    },
    salesforce: {
      status: "warn-amber",
      title: "Salesforce CPQ add-on",
      sub: "Enterprise CPQ add-on license starting at ₹6,000+/user/mo",
    },
    leadsquared: {
      status: "close-gray",
      title: "Not available",
      sub: "Requires separate accounting & billing software",
    },
  },
  {
    feature: "Hourly Telemetry & Live Break Monitoring",
    desc: "Real-time WebSocket presence, rep activity timelines & break duration tracking (9AM–7PM).",
    dealvoice: {
      status: "check-green",
      title: "Real-time live WebSocket sales radar",
      sub: "Instant activity status & floor sync",
    },
    exotel_telephony: {
      status: "dash-amber",
      title: "Agent call logs only",
      sub: "No deal or break presence sync",
    },
    spreadsheets: {
      status: "close-gray",
      title: "Zero live team visibility",
      sub: "Complete sales floor blind spot",
    },
    zoho_crm: {
      status: "close-gray",
      title: "Login/logout stamps only",
      sub: "No live real-time rep status or break radar",
    },
    salesforce: {
      status: "close-gray",
      title: "Omni-Channel add-on",
      sub: "Requires Omni-Channel routing & supervisor console license",
    },
    leadsquared: {
      status: "dash-amber",
      title: "Check-in/out stamps only",
      sub: "No real-time WebSocket live activity timeline",
    },
  },
  {
    feature: "Setup & Go-Live Speed",
    desc: "Time required to onboard sales closers and begin tracking live deals without developer support.",
    dealvoice: {
      status: "check-green",
      title: "Instant self-serve (< 2 minutes)",
      sub: "Download app, sign in, start selling",
    },
    exotel_telephony: {
      status: "clock-amber",
      title: "1–2 weeks for trunk approval",
      sub: "Complex DID routing & KYC delays",
    },
    spreadsheets: {
      status: "check-gray",
      title: "Instant setup but zero scale",
      sub: "Breaks immediately past 2 reps",
    },
    zoho_crm: {
      status: "clock-amber",
      title: "2–4 weeks configuration",
      sub: "Requires implementation consultant & custom field mapping",
    },
    salesforce: {
      status: "clock-amber",
      title: "2–6 months deployment",
      sub: "Requires certified Salesforce admin / consultant setup",
    },
    leadsquared: {
      status: "clock-amber",
      title: "2–4 weeks onboarding",
      sub: "Requires assisted setup & custom migration workflows",
    },
  },
  {
    feature: "All-in-One Predictable Pricing",
    desc: "Transparent per-user pricing with no hidden CTI license or surge charges.",
    dealvoice: {
      status: "check-green",
      title: "All-in-one ₹799/mo",
      sub: "Zero hidden CTI or per-minute fees",
      subHighlight: "green",
    },
    exotel_telephony: {
      status: "close-gray",
      title: "High base rental",
      sub: "+ Unpredictable per-minute calling bills",
    },
    spreadsheets: {
      status: "close-gray",
      title: '"Free" software',
      sub: "Huge revenue leak from lost deals",
      subHighlight: "red",
    },
    zoho_crm: {
      status: "close-gray",
      title: "Base + Add-on fees",
      sub: "Telephony, CPQ and analytics sold separately",
    },
    salesforce: {
      status: "close-gray",
      title: "Enterprise tier (₹12,000+/user)",
      sub: "Heavy annual commitments + per-module add-on licenses",
    },
    leadsquared: {
      status: "close-gray",
      title: "Annual contract locks",
      sub: "Expensive per-telecaller add-on pricing tiers",
    },
  },
];

function PricingHeroSection({ openSignup, openTrialSignup }) {
  const [selectedCompetitors, setSelectedCompetitors] = useState([
    "exotel_telephony",
    "spreadsheets",
  ]);

  const toggleCompetitor = (id) => {
    if (selectedCompetitors.includes(id)) {
      if (selectedCompetitors.length > 1) {
        setSelectedCompetitors(selectedCompetitors.filter((c) => c !== id));
      }
    } else {
      if (selectedCompetitors.length < 2) {
        setSelectedCompetitors([...selectedCompetitors, id]);
      } else {
        // Keep second and add new selection
        setSelectedCompetitors([selectedCompetitors[1], id]);
      }
    }
  };

  const renderIcon = (status) => {
    switch (status) {
      case "check-green":
        return (
          <span className="table-icon-wrap icon-check-green">
            <CheckOutlined />
          </span>
        );
      case "warn-amber":
        return (
          <span className="table-icon-wrap icon-warn-amber">
            <InrOutlined />
          </span>
        );
      case "dash-amber":
        return (
          <span className="table-icon-wrap icon-dash-amber">
            <MinusOutlined />
          </span>
        );
      case "clock-amber":
        return (
          <span className="table-icon-wrap icon-clock-amber">
            <ClockCircleOutlined />
          </span>
        );
      case "check-gray":
        return (
          <span className="table-icon-wrap icon-check-gray">
            <CheckOutlined />
          </span>
        );
      case "close-gray":
      default:
        return (
          <span className="table-icon-wrap icon-close-gray">
            <CloseOutlined />
          </span>
        );
    }
  };

  const renderCellContent = (item, isDealVoice = false) => {
    if (!item) return null;
    return (
      <div className={`table-cell-layout ${isDealVoice ? "dv-cell-layout" : ""}`}>
        {renderIcon(item.status)}
        <div className="table-cell-text-block">
          <span className={`cell-title ${isDealVoice ? "dv-cell-title" : ""}`}>
            {item.title}
          </span>
          {item.sub && (
            <span
              className={`cell-sub ${
                item.subHighlight === "green"
                  ? "cell-sub-green"
                  : item.subHighlight === "red"
                  ? "cell-sub-red"
                  : ""
              }`}
            >
              {item.sub}
            </span>
          )}
        </div>
      </div>
    );
  };

  const comp1 = COMPETITORS.find((c) => c.id === selectedCompetitors[0]);
  const comp2 = COMPETITORS.find((c) => c.id === selectedCompetitors[1]);

  return (
    <section className="pricing-hero-wrapper" id="pricing-hero">
      <div className="pricing-hero-outer-frame">
        {/* TOP SECTION HEADER WITH NATURAL DOODLE ARROW */}
        <div className="pricing-hero-header-wrap">
          <span className="section-eyebrow-badge">PLATFORM COMPARISON</span>
          <div className="pricing-title-doodle-row">
            <Title level={1} className="pricing-hero-title">
              <span>Why People <span className="pricing-title-choose-highlight">Choose</span></span>
              <img
                src={dealVoiceLogo}
                alt="DealVoice"
                className="pricing-title-logo-img"
              />
            </Title>

            {/* HAND-DRAWN DOODLE ARROW WITH RIGHT-ALIGNED COMPARE LABEL */}
            <div className="compare-doodle-wrapper" aria-hidden="true">
              <svg
                width="240"
                height="135"
                viewBox="0 0 240 135"
                fill="none"
                className="compare-doodle-arrow-svg"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Doodle arrow: starts next to logo, loops, and reaches down directly to selection tab */}
                <path
                  d="M 4 52 C 38 18, 90 8, 138 24 C 168 34, 172 65, 146 65 C 120 65, 116 38, 140 26 C 170 12, 204 45, 214 118"
                  stroke="#475569"
                  strokeWidth="2.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M 200 108 L 214 124 L 228 110"
                  stroke="#475569"
                  strokeWidth="2.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              <span className="compare-handwritten-label">Compare</span>
            </div>
          </div>

          <Paragraph className="pricing-hero-subtitle">
            Everything high velocity sales teams need automated Android telephony sync, and unified quotation-to-invoice closing in one workspace.
          </Paragraph>
        </div>

        {/* COMPETITOR SELECTOR TOOLBAR */}
        <div className="compare-toolbar-wrap">
          <div className="competitor-segmented-tabbar" role="tablist" aria-label="Competitor comparison selection">
            {COMPETITORS.map((comp) => {
              const isSelected = selectedCompetitors.includes(comp.id);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  key={comp.id}
                  className={`competitor-tab-item ${isSelected ? "tab-active" : ""}`}
                  onClick={() => toggleCompetitor(comp.id)}
                >
                  {isSelected && <span className="tab-blue-dot">•</span>}
                  <span>{comp.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* COMPARISON TABLE: CRISP GRID WITH 0 BORDER RADIUS & 0 SHADOW */}
        <div className="pricing-compare-table-container">
          <div className="compare-grid-table">
            {/* TABLE HEADER ROW */}
            <div className="compare-grid-header">
              {/* Col 1: Core Architecture */}
              <div className="compare-col-head col-feature-head">
                <span className="head-eyebrow">CORE ARCHITECTURE</span>
                <span className="head-title">Capability &amp; Sales Velocity</span>
              </div>

              {/* Col 2: DealVoice (Recommended) */}
              <div className="compare-col-head col-dealvoice-head">
                <div className="dv-recommended-badge">
                  RECOMMENDED SOLUTION
                </div>
                <div className="dv-brand-row">
                  <span className="dv-brand-name">
                    DealVoice <span className="dv-tm">™</span>
                  </span>
                  <span className="dv-badge-pill">ALL-IN-ONE</span>
                </div>
                <div className="dv-price-row">
                  <span className="dv-price-val">From ₹799</span>
                  <span className="dv-price-unit"> / user / mo</span>
                </div>
              </div>

              {/* Col 3: Competitor 1 */}
              <div className={`compare-col-head col-comp-head ${!comp2 ? "col-comp-last" : ""}`}>
                <span className="comp-head-title">{comp1?.name}</span>
                <span className="comp-head-sub">{comp1?.subtitle}</span>
              </div>

              {/* Col 4: Competitor 2 (Only if selected) */}
              {comp2 && (
                <div className="compare-col-head col-comp-head col-comp-last">
                  <span className="comp-head-title">{comp2.name}</span>
                  <span className="comp-head-sub">{comp2.subtitle}</span>
                </div>
              )}
            </div>

            {/* TABLE BODY ROWS */}
            <div className="compare-grid-body">
              {COMPARISON_ROWS.map((row, idx) => (
                <div className="compare-grid-row" key={idx}>
                  {/* Col 1: Feature Title & Desc */}
                  <div className="compare-col-cell col-feature-cell">
                    <span className="feature-row-title">{row.feature}</span>
                    <span className="feature-row-desc">{row.desc}</span>
                  </div>

                  {/* Col 2: DealVoice Cell */}
                  <div className="compare-col-cell col-dealvoice-cell">
                    {renderCellContent(row.dealvoice, true)}
                  </div>

                  {/* Col 3: Competitor 1 Cell */}
                  <div className={`compare-col-cell col-comp-cell ${!comp2 ? "col-comp-last" : ""}`}>
                    {renderCellContent(row[selectedCompetitors[0]])}
                  </div>

                  {/* Col 4: Competitor 2 Cell (Only if selected) */}
                  {comp2 && (
                    <div className="compare-col-cell col-comp-cell col-comp-last">
                      {renderCellContent(row[selectedCompetitors[1]])}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* TABLE FOOTER / CTA BAR */}
            <div className="compare-grid-footer">
              <div className="compare-footer-left">
                <span className="footer-headline">Ready to accelerate your pipeline velocity?</span>
                <div className="footer-meta-badges">
                  <span>Instant 2-minute setup</span>
                  <span className="meta-dot">•</span>
                  <span>No credit card required</span>
                  <span className="meta-dot">•</span>
                  <span>SOC-2 Type II Certified</span>
                </div>
              </div>
              <div className="compare-footer-right">
                <Button
                  type="text"
                  className="footer-demo-btn"
                  onClick={() => (typeof openTrialSignup === "function" ? openTrialSignup("") : openSignup(false))}
                >
                  Book Architecture Demo
                </Button>
                <Button
                  type="primary"
                  className="footer-trial-btn"
                  onClick={() => (typeof openTrialSignup === "function" ? openTrialSignup("") : openSignup(false))}
                >
                  Start 14-Day Free Trial
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PricingHeroSection;
